import * as R from 'remeda';
import { Queue } from '@rossum/api-client/queues';
import { SchemaField } from '@rossum/api-client/schemaFields';
import {
  AnnotationListColumns,
  AnnotationListTable,
  SchemaColumn,
} from '@rossum/api-client/shared';
import { useSetFetchedSchemaFields } from '../../document-list-columns/hooks/useFetchSchemaFields';
import {
  DEFAULT_COLUMN_WIDTH,
  getColumnField,
  getColumnName,
} from '../../document-list-columns/utils';
import { usePatchTableConfig } from './usePatchTableConfig';
import { OrgTableSettingsKey } from './useTableConfig';

type Props = AnnotationListTable & {
  activeQueue: Queue | null;
  settingsKey?: OrgTableSettingsKey;
};

// pass only unique columns by columnType and field properties
// we do not support columns with same field and different dataType for now
export const uniqColumns = (columns: AnnotationListColumns) =>
  R.uniqueBy(columns, c =>
    getColumnField({ columnType: c.columnType, field: getColumnName(c) })
  );

export const useCustomizeColumns = ({
  columns,
  activeQueue,
  settingsKey,
}: Props) => {
  const { updateColumns } = usePatchTableConfig({ activeQueue, settingsKey });

  const setFetchedSchemaFields = useSetFetchedSchemaFields();

  const handleAddColumn = (selectedSchemaField: SchemaField) => {
    const asSchemaColumn: SchemaColumn = {
      columnType: 'schema',
      dataType: selectedSchemaField.type ?? 'string',
      schemaId: selectedSchemaField.schemaId ?? '',
      width: DEFAULT_COLUMN_WIDTH,
      visible: true,
    };

    const uniqueColumns = uniqColumns([asSchemaColumn, ...columns]);

    // this runs before the update because otherwise for a brief second we see the 'schema_id' on the table header instead of 'label'
    // e.g. total_amount instead of Total Amount
    setFetchedSchemaFields(response => {
      if (!response) return response;

      return {
        ...response,
        results: [...response.results, selectedSchemaField],
      };
    });

    updateColumns(uniqueColumns);
  };

  const handleRemoveColumn = (selectedColumn: SchemaColumn) => {
    const filteredColumns = columns.filter(
      c => getColumnName(c) !== selectedColumn.schemaId
    );
    updateColumns(filteredColumns);
  };

  return { handleAddColumn, handleRemoveColumn, updateColumns };
};
